import { Cesium } from 'app/cesium';
import { TerrainService } from 'app/map/services/terrain.service';
import { LocationStats } from 'app/shared/collections/location_stat';
import { Processor } from 'app/shared/collections/time/processor/Processor';
import { Toast } from 'app/shared/toast/toast.service';
import {
    closestPastSample,
    getValue,
    Interpolator,
    TimeSeriesCollection,
    TimeSeriesCollectionInterface,
} from 'time-series-collection';
import { TerrainLoadedStateProvider } from 'app/map/services/terrain_state_provider';

/**
 * Returns whether or not a cartographic location is valid
 */
function isLocationValid(cartographicLocation: Cesium.Cartographic): boolean {
    if (!cartographicLocation) {
        return false;
    }
    const lat = Cesium.Math.toDegrees(cartographicLocation.latitude);
    const lon = Cesium.Math.toDegrees(cartographicLocation.longitude);
    return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

/**
 * A simpler interface than an actual location stat, easier for testing and passing around.
 * We pass both the cartographic and cartesian as you can't convert back and forth between them and get the same result.
 */
interface ILocationStat {
    cartesianLocation?: Cesium.Cartesian3;
    cartographicLocation?: Cesium.Cartographic;
    assetId?: number;
    timestamp?: string;
    // yep, we keep track of the original location stat anyway otherwise we can never retrieve it
    originalLocationStat?: LocationStats;
}

function createLocationInterpolator(cutoffSeconds: number): Interpolator<ILocationStat> {
    return (
        collection: TimeSeriesCollectionInterface<ILocationStat>,
        targetTimestamp: number,
        targetIndex: number,
    ) => locationInterpolator(collection, targetTimestamp, targetIndex, cutoffSeconds);
}

/**
 * an interpolator for cartesian locations
 * @param collection
 * @param targetTimestamp
 * @param targetIndex
 * @param cutoffSeconds
 */
function locationInterpolator(
    collection: TimeSeriesCollectionInterface<ILocationStat>,
    targetTimestamp: number,
    targetIndex: number,
    cutoffSeconds: number,
) {
    if (!collection.timestamps.length) {
        // if we have no samples at all, return no location
        return undefined;
    }
    if (targetIndex === 0) {
        // if we're looking for something at index 0, and we have samples,
        // then it must mean we're targeting something before the earliest sample we have.
        // Don't return anything
        return undefined;
    }
    if (targetIndex === collection.timestamps.length) {
        // if we're looking for a sample just outside of our range,
        // it means we're targeting something later than any sample we have.
        // Just return the last sample we have even though it could be from ages in the past.
        return collection.datums[targetIndex - 1];
    }

    // now we can safely grab the sample before and the sample after our target timestamp
    const sampleBefore = collection.datums[targetIndex - 1];
    const sampleAfter = collection.datums[targetIndex];
    const timestampBefore = collection.timestamps[targetIndex - 1];
    const timestampAfter = collection.timestamps[targetIndex];
    const secondsBetweenLastAndNextTime = timestampAfter - timestampBefore;

    // if we're about to interpolate over a distance of more than our cutoff
    if (secondsBetweenLastAndNextTime > cutoffSeconds) {
        // just use the last sample we have
        return sampleBefore;
    }

    // calculate the percentage we are between the two samples
    const amount = (targetTimestamp - timestampBefore) / secondsBetweenLastAndNextTime;

    // linearly interpolate each property
    return {
        cartesianLocation: new Cesium.Cartesian3(
            lerp(sampleBefore.cartesianLocation.x, sampleAfter.cartesianLocation.x, amount),
            lerp(sampleBefore.cartesianLocation.y, sampleAfter.cartesianLocation.y, amount),
            lerp(sampleBefore.cartesianLocation.z, sampleAfter.cartesianLocation.z, amount),
        ),
        cartographicLocation: null,
    };
}

function lerp(value1: number, value2: number, alpha: number): number {
    // (1 - alpha) * a + alpha * b
    return (1 - alpha) * value1 + alpha * value2;
}

function timestampStringToUnix(timestamp: string): number {
    const msSinceEpoch = Date.parse(timestamp);
    return isNaN(msSinceEpoch) ? null : msSinceEpoch / 1000;
}

export class LocationProcessor implements Processor<LocationStats> {
    private readonly _terrainService: TerrainService;
    private _tsc: { [x: number]: TimeSeriesCollection<ILocationStat> };
    private _locationInterpolationCutoffSeconds: number;
    private _onInvalidLocationStats: (stats: Array<LocationStats>) => void;
    private _terrainLoadedStateProvider: TerrainLoadedStateProvider;
    private readonly _sampleTerrain: (
        terrainProvider: Cesium.TerrainProvider,
        terrainDetailLevel: number,
        locations: Array<Cesium.Cartographic>,
    ) => Promise<Cesium.Cartographic[]>;

    constructor(
        terrainService: TerrainService,
        locationInterpolationCutoffSeconds: number,
        terrainLoadedStateProvider: TerrainLoadedStateProvider,
        onInvalidLocationStats?: (stats: Array<LocationStats>) => void,
        sampleTerrain?: (locations: Array<Cesium.Cartographic>) => unknown,
    ) {
        this._terrainService = terrainService;
        this._terrainLoadedStateProvider = terrainLoadedStateProvider;
        this._onInvalidLocationStats = onInvalidLocationStats || (() => null);
        this._sampleTerrain = sampleTerrain || Cesium.sampleTerrain;

        this._terrainLoadedStateProvider.addTaskToTerrainStateChangeQueue(async () => {
            // default it to infinity meaning interpolate forever, 0 will mean no interpolation
            this._locationInterpolationCutoffSeconds = isNaN(locationInterpolationCutoffSeconds)
                ? Infinity
                : locationInterpolationCutoffSeconds;

            const giveMeAllTheBaconAndEggsYouHave: Array<ILocationStat> = [];
            // go through every stat we currently know about, and reset it's cesium height
            for (const key in this._tsc) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                giveMeAllTheBaconAndEggsYouHave.push(...this._tsc[key]._state.datums);
            }

            // update all these things with new heights!/
            // just mutate away my friend!
            await this.sampleStatHeights(giveMeAllTheBaconAndEggsYouHave, this._terrainService);
        });

        this._tsc = {};
    }

    async add(stats: Array<LocationStats>): Promise<void> {
        const validStats: Array<ILocationStat> = [];
        const invalidStats: Array<LocationStats> = [];

        let stat: ILocationStat;
        let locationStat: LocationStats;
        let lon: number, lat: number;

        for (let i = 0; i < stats.length; i++) {
            locationStat = stats[i];
            if (locationStat.location) {
                lon = locationStat.location[0];
                lat = locationStat.location[1];
                stat = {
                    assetId: locationStat.assetId,
                    timestamp: locationStat.timestamp,
                    originalLocationStat: locationStat,
                    cartographicLocation: Cesium.Cartographic.fromDegrees(lon, lat, 0.01),
                };
                if (isLocationValid(stat.cartographicLocation)) {
                    validStats.push(stat);
                } else {
                    invalidStats.push(locationStat);
                }
            }
        }

        if (invalidStats.length) {
            this._onInvalidLocationStats(invalidStats);
        }

        // this will mutate the list of stats, adding the correct cartesian value
        await this.sampleStatHeights(validStats, this._terrainService);

        // now actually add the stats to a collection for each asset
        for (let i = 0; i < validStats.length; i++) {
            stat = validStats[i];
            let tsc = this._tsc[stat.assetId];
            if (!tsc) {
                // and create a collection if need
                tsc = this._tsc[stat.assetId] = new TimeSeriesCollection<ILocationStat>(
                    createLocationInterpolator(this._locationInterpolationCutoffSeconds),
                );
            }
            const timestamp = timestampStringToUnix(stat.timestamp);
            if (timestamp && stat.cartesianLocation) {
                tsc.addSample(timestamp, stat);
            }
        }
    }

    remove(start: number, end: number): Promise<void> {
        for (const collection of Object.values(this._tsc)) {
            collection.removeOutsideTimeFrame(start, end, true);
        }
        return Promise.resolve();
    }

    getCountOfPositions(assetId: number): number {
        const col = this._tsc[assetId];
        return col ? col.size() : 0;
    }

    hasAnyPositions(assetId: number): boolean {
        const col = this._tsc[assetId];
        return col ? col.size() > 0 : false;
    }

    getAssetPositionUnix(assetId: number, time: number): Cesium.Cartesian3 | null {
        const col = this._tsc[assetId];
        const stat = col ? col.getValue(time) : null;
        return stat ? stat.cartesianLocation : null;
    }

    getAssetLocationStatUnix(assetId: number, time: number): LocationStats | null {
        const col = this._tsc[assetId];
        const stat = col ? col.getValue(time) : null;
        return stat ? stat.originalLocationStat : null;
    }

    public async sampleStatHeights(
        stats: Array<ILocationStat>,
        terrainService: TerrainService,
    ): Promise<void> {
        if (!terrainService.mapTerrainEnabled) {
            stats.forEach((stat) => {
                // reset our height back to ground level
                stat.cartographicLocation.height = 0.01;
                // and recalculate our cartesian based off that
                stat.cartesianLocation = Cesium.Ellipsoid.WGS84.cartographicToCartesian(
                    stat.cartographicLocation,
                );
            });
        } else {
            try {
                // sample all the cartographics using the current terrain
                const cartographics = stats.map((stat) => stat.cartographicLocation);

                if (terrainService.terrainConfigType == 'arcgis') {
                    // Yeah, having an asset in the massive hole in Antarctica and Santa's Workshop
                    // for some terrain data will cause an error... so push them North or South a bit.
                    // This is a workaround to fix a crash in Cesium for points lying outside the rectangle
                    const arcGisLatitudeLimit = 1.48442222974;
                    cartographics.forEach((cart) => {
                        if (cart.latitude < -arcGisLatitudeLimit) {
                            cart.latitude = -arcGisLatitudeLimit;
                        } else if (cart.latitude > arcGisLatitudeLimit) {
                            cart.latitude = arcGisLatitudeLimit;
                        }
                    });
                }

                const cesiumPromise = this._sampleTerrain(
                    terrainService.cesiumTerrainProvider,
                    terrainService.terrainDetailLevel,
                    cartographics,
                );
                // wait for that promise to come back
                await cesiumPromise;

                // and then just smash the new properties back over the passed in array! it's fine!
                cartographics.forEach((cartographic, i) => {
                    if (cartographic.height !== undefined) {
                        stats[i].cartesianLocation =
                            Cesium.Ellipsoid.WGS84.cartographicToCartesian(cartographic);
                    }
                });
            } catch (error) {
                // if the promise fails just log and bail, no heights should be sampled
                console.error(
                    'Unable to sample terrain heights, there may be invalid location stats!',
                );
                console.error(error);
                // Toast uses slightly more user-friendly language than the console-error
                new Toast(
                    Toast.ERROR,
                    `Error calculating terrain heights, there may be invalid Asset location data!`,
                );
            }
        }
    }

    clear() {
        this._tsc = {};
    }

    get(key: number, time: number): Array<LocationStats> {
        const col = this._tsc[key];
        if (!col) {
            return [];
        }
        // TODO Daniel - we need something to make this better
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const state: TimeSeriesCollectionInterface<ILocationStat> = col._state;
        const stat = getValue(state, time, closestPastSample(Infinity));
        return stat && stat.originalLocationStat ? [stat.originalLocationStat] : [];
    }

    getAll(): Array<LocationStats> {
        const giveMeAllTheBaconAndEggsYouHave: Array<ILocationStat> = [];
        for (const key in this._tsc) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            giveMeAllTheBaconAndEggsYouHave.push(...this._tsc[key]._state.datums);
        }
        return giveMeAllTheBaconAndEggsYouHave.map((stat) => stat.originalLocationStat);
    }

    getAllForKey(key: number): Array<LocationStats> {
        const c = this._tsc[key];
        if (c) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const stats: Array<ILocationStat> = c._state.datums;
            return stats.map((s) => s.originalLocationStat);
        }
        return [];
    }
}
